import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "mandala-bloom";

const DEFAULT_BG = "#0c0a1f";
const DEFAULT_PETAL_A = "#f59e0b";
const DEFAULT_PETAL_B = "#a78bfa";

const DRAW_DURATION_MS = 5500;     // how long it takes to draw one mandala
const HOLD_DURATION_MS = 1400;     // pause showing the finished mandala
const FADE_DURATION_MS = 1800;     // gentle fade-out before the next blooms

const STEPS_PER_FRAME = 6;         // mini-segments drawn per RAF tick

function rand(min, max) { return min + Math.random() * (max - min); }
function randInt(min, max) { return Math.floor(rand(min, max + 1)); }

export default function MandalaBloom({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_PETAL_A, DEFAULT_PETAL_B]);
  const stateRef = useRef({
    mandala: null,            // current mandala parameters
    phase: "draw",            // "draw" | "hold" | "fade"
    phaseStartTs: 0,
    progress: 0,              // current pen-position parameter (0..1)
  });
  // The drawn strokes accumulate on a *separate* offscreen canvas so we
  // can fade them out smoothly during the "fade" phase by drawing the
  // offscreen canvas with a falling globalAlpha, instead of repainting
  // every stroke each frame (which would be expensive).
  const offRef = useRef(null);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_PETAL_A),
      pickColor(colors, 2, DEFAULT_PETAL_B),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const off = document.createElement("canvas");
    offRef.current = off;
    const offCtx = off.getContext("2d");
    if (!offCtx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    // Generate a fresh mandala. The "guide curve" defines the path of one
    // arm; the N-fold symmetry replicates that arm around the center.
    const newMandala = () => {
      const symmetry = randInt(6, 14);   // 6..14 fold symmetry
      const radiusMax = Math.min(width, height) * rand(0.25, 0.42);
      // Layered curves: each layer has its own frequency so the result is
      // a mix of petals, scallops, swirls.
      const layers = [];
      const layerCount = randInt(2, 4);
      for (let i = 0; i < layerCount; i += 1) {
        layers.push({
          freq: randInt(2, 7),                 // sine wiggles per arm
          radial: rand(0.4, 1.0) * radiusMax,
          twist: rand(0, Math.PI * 2),
          width: rand(1.0, 2.0),
          colorIdx: Math.random() < 0.5 ? 1 : 2,
          // Each layer fills along a slightly different segment of the arm
          // so the mandala builds up in rings rather than all at once.
          startT: rand(0, 0.25),
          endT: 1,
        });
      }
      return {
        cx: width / 2,
        cy: height / 2,
        symmetry,
        radiusMax,
        layers,
      };
    };

    const resetCanvas = () => {
      offCtx.setTransform(1, 0, 0, 1, 0, 0);
      offCtx.clearRect(0, 0, off.width, off.height);
      offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      off.width = canvas.width;
      off.height = canvas.height;
      offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      stateRef.current.mandala = newMandala();
      stateRef.current.phase = "draw";
      stateRef.current.phaseStartTs = 0;
      stateRef.current.progress = 0;
      resetCanvas();
    };

    // Sample one point on the guide curve at parameter u in [0..1].
    // u maps to a radial sweep along one arm: 0 at the centre, 1 at the
    // outer rim. The layer's `freq` adds the sinusoidal petal wiggle.
    const sampleArm = (layer, u) => {
      const r = layer.radial * u;
      // Angle within one arm (a small sweep, then symmetry replicates it).
      // Adding `sin(freq * π * u)` gives a petal that bulges outward.
      const armAngle = layer.twist + Math.sin(layer.freq * Math.PI * u) * 0.6;
      return { r, armAngle };
    };

    const drawSegment = (mandala, layer, u0, u1) => {
      const [, palA, palB] = colorsRef.current;
      const color = layer.colorIdx === 1 ? palA : palB;
      const a = sampleArm(layer, u0);
      const b = sampleArm(layer, u1);
      offCtx.strokeStyle = rgba(color, 0.85);
      offCtx.lineWidth = layer.width;
      offCtx.lineCap = "round";

      // Replicate the segment around all `symmetry` arms.
      const armStep = (Math.PI * 2) / mandala.symmetry;
      for (let s = 0; s < mandala.symmetry; s += 1) {
        const baseAngle = s * armStep;
        const ang0 = baseAngle + a.armAngle;
        const ang1 = baseAngle + b.armAngle;
        const x0 = mandala.cx + Math.cos(ang0) * a.r;
        const y0 = mandala.cy + Math.sin(ang0) * a.r;
        const x1 = mandala.cx + Math.cos(ang1) * b.r;
        const y1 = mandala.cy + Math.sin(ang1) * b.r;
        offCtx.beginPath();
        offCtx.moveTo(x0, y0);
        offCtx.lineTo(x1, y1);
        offCtx.stroke();
      }
    };

    const tick = (ts) => {
      const state = stateRef.current;
      const [bg] = colorsRef.current;

      if (!state.mandala) state.mandala = newMandala();
      if (state.phaseStartTs === 0) state.phaseStartTs = ts;
      const phaseAge = ts - state.phaseStartTs;

      if (state.phase === "draw") {
        // Advance the pen along its guide curve and stamp tiny segments
        // for each layer that's currently within its [startT..endT].
        const dPerStep = 1 / (DRAW_DURATION_MS / 16) / state.mandala.layers.length;
        for (let step = 0; step < STEPS_PER_FRAME; step += 1) {
          const u0 = state.progress;
          const u1 = state.progress + dPerStep * 0.9;
          for (let i = 0; i < state.mandala.layers.length; i += 1) {
            const layer = state.mandala.layers[i];
            if (u0 >= layer.startT && u0 <= layer.endT) {
              drawSegment(state.mandala, layer, u0, u1);
            }
          }
          state.progress = Math.min(1, state.progress + dPerStep);
          if (state.progress >= 1) break;
        }
        if (phaseAge >= DRAW_DURATION_MS || state.progress >= 1) {
          state.phase = "hold";
          state.phaseStartTs = ts;
        }
      } else if (state.phase === "hold") {
        if (phaseAge >= HOLD_DURATION_MS) {
          state.phase = "fade";
          state.phaseStartTs = ts;
        }
      } else if (state.phase === "fade") {
        if (phaseAge >= FADE_DURATION_MS) {
          state.phase = "draw";
          state.phaseStartTs = ts;
          state.progress = 0;
          state.mandala = newMandala();
          resetCanvas();
        }
      }

      // Composite: bg fill, then the offscreen mandala overlaid with a
      // global alpha that drops during the fade phase.
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      let alpha = 1;
      if (state.phase === "fade") {
        alpha = 1 - phaseAge / FADE_DURATION_MS;
      }
      ctx.globalAlpha = Math.max(0, alpha);
      // Draw the (DPR-scaled) offscreen at logical size — it's sized to
      // match the visible canvas so we map 1:1 here.
      ctx.drawImage(off, 0, 0, width, height);
      ctx.globalAlpha = 1;

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
